import { isIdpPosition } from "./draft-position-filter";

const OFFENSIVE_KEEPER_POSITIONS = new Set(["QB", "RB", "WR", "TE"]);

export interface KeeperOptionIdentity {
  eligibleId: string;
}

export interface KeeperRecommendationIdentity extends KeeperOptionIdentity {
  positions?: readonly string[];
}

/**
 * Compare the canonical slot values shown in the keeper form with the last
 * server-confirmed save. This intentionally treats trailing missing entries
 * as empty slots so a fixed-length form cannot become dirty merely because a
 * response omitted its unused tail.
 */
export function keeperSlotsAreDirty(
  slots: readonly (string | null)[],
  savedSlots: readonly (string | null)[],
): boolean {
  const length = Math.max(slots.length, savedSlots.length);
  for (let index = 0; index < length; index += 1) {
    if ((slots[index] ?? null) !== (savedSlots[index] ?? null)) return true;
  }
  return false;
}

function isIdpOnly(eligible: KeeperRecommendationIdentity): boolean {
  const positions = eligible.positions ?? [];
  const hasIdpPosition = positions.some(isIdpPosition);
  const hasOffensivePosition = positions.some((position) => (
    OFFENSIVE_KEEPER_POSITIONS.has(position.toLocaleUpperCase("en-US"))
  ));
  return hasIdpPosition && !hasOffensivePosition;
}

/**
 * Remove empty gaps and duplicate player ids without changing the relative
 * order of the remaining selections. Keeper forms always render a fixed
 * number of selectors, so the unused positions are padded with trailing nulls.
 */
export function compactKeeperSlots(
  slots: readonly (string | null)[],
  keeperLimit = slots.length,
): Array<string | null> {
  const selected: string[] = [];
  const seen = new Set<string>();

  for (const value of slots) {
    if (!value || seen.has(value) || selected.length >= keeperLimit) continue;
    seen.add(value);
    selected.push(value);
  }

  return Array.from({ length: keeperLimit }, (_, index) => selected[index] ?? null);
}

/**
 * Apply one selector change and return canonical keeper-slot state. Clearing a
 * middle selector rotates later selections upward. Selecting a player already
 * present in stale client state moves that player instead of creating a
 * duplicate; the picker UI also filters selected players from other selectors.
 */
export function updateKeeperSlot(
  slots: readonly (string | null)[],
  slotIndex: number,
  eligibleId: string | null,
): Array<string | null> {
  if (!Number.isInteger(slotIndex) || slotIndex < 0 || slotIndex >= slots.length) {
    throw new RangeError("Keeper slot index is out of range.");
  }

  const nextValue = eligibleId || null;
  const updated = [...slots];
  if (nextValue) {
    for (let index = 0; index < updated.length; index += 1) {
      if (index !== slotIndex && updated[index] === nextValue) updated[index] = null;
    }
  }
  updated[slotIndex] = nextValue;
  return compactKeeperSlots(updated);
}

/**
 * Keep each eligible player in at most one keeper selector while retaining the
 * current selector's value. The server still validates uniqueness on save.
 */
export function keeperOptionsForSlot<T extends KeeperOptionIdentity>(
  eligibles: readonly T[],
  slots: readonly (string | null)[],
  slotIndex: number,
): T[] {
  const currentValue = slots[slotIndex] ?? null;
  const selectedElsewhere = new Set(
    slots.filter(
      (value, index): value is string => index !== slotIndex && Boolean(value),
    ),
  );

  return eligibles.filter(
    (eligible) => eligible.eligibleId === currentValue
      || !selectedElsewhere.has(eligible.eligibleId),
  );
}

/**
 * Preserve existing manual choices, then consume the recommendation order
 * generated by Draft Lab's canonical keeper scorer. The engine-ranked order is
 * preserved exactly, including its modeled IDP values and offensive/defensive
 * hybrids. IDs outside the returned ranked target use a deterministic fallback
 * tier; within that tier, pure IDPs follow non-IDPs and stable server order
 * breaks ties. The production worker separately fails closed if a current
 * eligible id is absent from the canonical catalog. Existing manual choices
 * always remain at the front.
 */
export function autoPopulateKeeperSlots<T extends KeeperRecommendationIdentity>(
  eligibles: readonly T[],
  slots: readonly (string | null)[],
  recommendedEligibleIds: readonly string[],
  keeperLimit: number,
): Array<string | null> {
  const eligibleById = new Map(eligibles.map((eligible) => [eligible.eligibleId, eligible]));
  const selected = compactKeeperSlots(slots, keeperLimit)
    .filter((value): value is string => value !== null && eligibleById.has(value));
  const selectedSet = new Set(selected);
  const canonicalRank = new Map<string, number>();
  for (const eligibleId of recommendedEligibleIds) {
    if (!canonicalRank.has(eligibleId)) canonicalRank.set(eligibleId, canonicalRank.size);
  }
  const eligibleOrder = new Map(eligibles.map((eligible, index) => [eligible.eligibleId, index]));
  const remaining = eligibles
    .filter((eligible) => !selectedSet.has(eligible.eligibleId))
    .sort((left, right) => {
      const leftCanonicalRank = canonicalRank.get(left.eligibleId);
      const rightCanonicalRank = canonicalRank.get(right.eligibleId);
      if (leftCanonicalRank !== undefined || rightCanonicalRank !== undefined) {
        if (leftCanonicalRank === undefined) return 1;
        if (rightCanonicalRank === undefined) return -1;
        return leftCanonicalRank - rightCanonicalRank;
      }

      const idpTier = Number(isIdpOnly(left)) - Number(isIdpOnly(right));
      if (idpTier !== 0) return idpTier;
      return (eligibleOrder.get(left.eligibleId) ?? 0) - (eligibleOrder.get(right.eligibleId) ?? 0);
    });
  const targetCount = Math.min(keeperLimit, eligibles.length);
  const populated = [
    ...selected,
    ...remaining.map((eligible) => eligible.eligibleId),
  ].slice(0, targetCount);

  return Array.from({ length: keeperLimit }, (_, index) => populated[index] ?? null);
}
